import {
  BaseRepository,
  IBaseModel,
  IListFetchParams,
  MN_SUPPLIERS,
  MN_TYPE_OF_PAYMENTS,
  MODULE,
} from '../common';
import type { Prisma } from '@prisma/client';
import * as z from 'zod';
import type { KeyValue } from '@jobcost-monorepo/utils';

type Supplier = Prisma.$SupplierPayload['scalars'];

export type ISupplier = Supplier &
  Omit<IBaseModel, 'createdAt' | 'updatedAt' | 'id'> & {
    typeOfPayment: KeyValue;
  };

export interface IListSupplierFetchParams extends IListFetchParams {
  typeOfPaymentId?: number;
}

class SupplierRepository extends BaseRepository<
  ISupplier,
  IListSupplierFetchParams
> {
  constructor(moduleName: MODULE) {
    super(moduleName);
    this.keyValueMapFields = {
      idlinkedField: 'id',
      descriptionlinkedField: ['name'],
    };
    this.gridFieldsOptions = [
      {
        visible: false,
        position: 0,
        linkedField: 'id',
        type: '',
        align: 'left',
        width: '12rem',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 1,
        linkedField: 'name',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 2,
        linkedField: 'typeOfPayment.name',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 3,
        linkedField: 'vat',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 4,
        linkedField: 'city',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
    ];

    this.gridFiltersOptions = [
      {
        position: { x: 0, y: 0 },
        linkedField: 'typeOfPaymentId',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_TYPE_OF_PAYMENTS,
        translationModuleName: MN_TYPE_OF_PAYMENTS,
      },
    ];

    this.formFieldsOptions = [
      {
        visible: false,
        readOnly: true,
        position: { x: 0, y: 0 },
        linkedField: 'id',
        type: 'string',
        width: '12rem',
        formatType: 'input',
        moduleName: MN_SUPPLIERS,
        translationModuleName: MN_SUPPLIERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 0 },
        linkedField: 'name',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_SUPPLIERS,
        translationModuleName: MN_SUPPLIERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 1 },
        linkedField: 'typeOfPaymentId',
        linkedFieldForDefaultValue: 'typeOfPayment.name',
        type: 'string',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_TYPE_OF_PAYMENTS,
        translationModuleName: MN_SUPPLIERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 2 },
        linkedField: 'address',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_SUPPLIERS,
        translationModuleName: MN_SUPPLIERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 2 },
        linkedField: 'city',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_SUPPLIERS,
        translationModuleName: MN_SUPPLIERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 3 },
        linkedField: 'postalCode',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_SUPPLIERS,
        translationModuleName: MN_SUPPLIERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 3 },
        linkedField: 'state',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_SUPPLIERS,
        translationModuleName: MN_SUPPLIERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 2, y: 3 },
        linkedField: 'country',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_SUPPLIERS,
        translationModuleName: MN_SUPPLIERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 4 },
        linkedField: 'vat',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_SUPPLIERS,
        translationModuleName: MN_SUPPLIERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 4 },
        linkedField: 'externalId',
        type: 'number',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_SUPPLIERS,
        translationModuleName: MN_SUPPLIERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 6 },
        linkedField: 'emailTimesheet',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_SUPPLIERS,
        translationModuleName: MN_SUPPLIERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 6 },
        linkedField: 'emailInvoice',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_SUPPLIERS,
        translationModuleName: MN_SUPPLIERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 2, y: 6 },
        linkedField: 'emailOrder',
        type: 'string',
        width: 'w-full',
        formatType: 'input',
        moduleName: MN_SUPPLIERS,
        translationModuleName: MN_SUPPLIERS,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 7 },
        linkedField: 'note',
        type: 'string',
        width: 'w-full',
        formatType: 'textArea',
        moduleName: MN_SUPPLIERS,
        translationModuleName: MN_SUPPLIERS,
      },
    ];

    // @ts-ignore
    this.formValidationSchema = z.object({
      id: z.number().nullable().optional(),
      name: z
        .string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .min(2, { message: 'min_2' })
        .max(50, { message: 'max_50' }),
      typeOfPaymentId: z
        .number({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .nullable()
        .optional(),
      address: z
        .string()
        .min(2, { message: 'min_2' })
        .max(50, { message: 'max_50' })
        .nullable()
        .optional(),
      postalCode: z
        .string()
        .min(5, { message: 'min_5' })
        .max(10, { message: 'max_10' })
        .nullable()
        .optional(),
      city: z
        .string()
        .min(2, { message: 'min_2' })
        .max(50, { message: 'max_50' })
        .nullable()
        .optional(),
      state: z
        .string()
        .min(2, { message: 'min_2' })
        .max(50, { message: 'max_50' })
        .nullable()
        .optional(),
      country: z
        .string()
        .min(2, { message: 'min_2' })
        .max(2, { message: 'max_2' })
        .nullable()
        .optional(),
      vat: z
        .string()
        .min(10, { message: 'min_10' })
        .max(20, { message: 'max_20' })
        .nullable()
        .optional(),
      externalId: z.number().nullable().optional(),
      emailTimesheet: z
        .string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .email({
          message: 'invalidEmail',
        })
        .nullable()
        .optional(),
      emailInvoice: z
        .string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .email({
          message: 'invalidEmail',
        })
        .nullable()
        .optional(),
      emailOrder: z
        .string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        })
        .email({
          message: 'invalidEmail',
        })
        .nullable()
        .optional(),
      note: z
        .string()
        .min(2, { message: 'min_2' })
        .max(255, { message: 'max_255' })
        .nullable()
        .optional(),
    });
  }
}

export default SupplierRepository;
